import type Luminary from '@lumen5/luminary';
import type {
    AspectRatio,
    OpaqueVideoData,
    SafeLuminary,
    Scene,
    SceneGroup,
    TimeAwareMediaAsset,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { ErrorReporter } from 'js/src/libs/error-reporter';
import {
    getDefaultMediaToReplace,
    isLuminaryEmpty,
    isVoiceDrivenSceneGroup,
} from 'js/src/luminary';
import type { ImmutableMap } from 'js/src/luminary/immutable-helpers';
import { ImmutableFromJS } from 'js/src/luminary/immutable-helpers';

/**
 * This function safely plays luminary in desired canvas parent
 * In case if canvas parent is not the same as current canvas parent
 */
export async function safePlay(
    luminary: SafeLuminary,
    {
        canvasParent,
        startTime,
        endTime,
    }: { canvasParent: HTMLElement | null; startTime: number; endTime: number },
) {
    await renderToCanvasSafely({
        luminary,
        canvasParent,
        operation: () => luminary.play({ startTime, endTime }),
    });
}

/**
 * This function safely plays a scene group in desired canvas parent in the case if canvas parent is not the same as current canvas parent.
 * Note that times are scene group relative. e.g. `{ startTime: 0, endTime: 1000 }` plays 1 second of the scene group.
 */
export async function safePlaySceneGroup(
    luminary: Luminary,
    {
        canvasParent,
        sceneGroup,
        startTime,
        endTime,
    }: {
        canvasParent: HTMLElement | null;
        sceneGroup: SceneGroup;
        startTime: number;
        endTime: number;
    },
) {
    await renderToCanvasSafely({
        luminary,
        canvasParent,
        operation: () => sceneGroup.play({ startTime, endTime }),
    });
}

/**
 * This function is built to avoid drawing black frames or frames from the previous scene
 * to target canvas.
 * When it completed it means that dynamic canvas is attached canvasParent and rendered
 * operation can be seek or play
 */
async function renderToCanvasSafely({
    luminary,
    canvasParent,
    operation,
}: {
    luminary: SafeLuminary;
    canvasParent: HTMLElement | null;
    operation: () => Promise<void>;
}) {
    let isGoingToRenderToAnotherCanvas = false;
    if (canvasParent !== luminary.canvasParent) {
        // prevent drawing something on the wrong scene
        // it will show static image preview of previous scene
        luminary.canvasParent = null;
        isGoingToRenderToAnotherCanvas = true;
    }

    await operation();

    if (isGoingToRenderToAnotherCanvas) {
        luminary.canvasParent = canvasParent;
    }
}

/**
 * This function adds validation to Luminary's save function and informs us if we're saving invalid video data
 */
export function saveLuminary(luminary: Luminary): ImmutableMap<OpaqueVideoData> {
    return ImmutableFromJS(saveRawLuminary(luminary));
}

export function saveRawLuminary(luminary: Luminary): OpaqueVideoData {
    // video data validation
    // The creator expects there to be at least one non-branding scene
    if (isLuminaryEmpty(luminary)) {
        throw new Error('No scenes in video');
    }
    if (luminary.sceneGroups.some((sceneGroup) => sceneGroup.scenes.length === 0)) {
        throw Error('Video contains scene groups with no scenes');
    }
    const { passed, message } = luminary.validateState();
    if (!passed) {
        ErrorReporter.capture(message);
    }

    return luminary.save();
}

export async function waitUntilTemplateBundleLoaded(luminary: SafeLuminary) {
    /**
     * Some operations require the template bundle to be loaded in the luminary instance.
     * This utility method will wait up to MAX_RETRIES * RETRY_DELAY seconds to ensure the template bundle is loaded
     * before proceeding. If the template isn't loaded after MAX_RETRIES, an error will be thrown.
     */
    // haven't loaded the template yet, wait for it to load
    const max_retries = 20;
    const retry_delay = 1000;
    let retries = 0;
    while (!luminary.isTemplateBundleSet && retries < max_retries) {
        await new Promise((resolve) => setTimeout(resolve, retry_delay));
        retries++;
    }
    if (!luminary.isTemplateBundleSet) {
        throw new Error('Template did not load – issue with loading?');
    }
}

export function getMediaToUpdate(
    scene: Scene,
    targetAssetId: string | undefined | null,
): TimeAwareMediaAsset | null {
    // if no specific media has been specified then update largest media asset
    if (targetAssetId) {
        const targetAsset = scene.getAssetById(targetAssetId);
        if (targetAsset && VideoData.isMediaAsset(targetAsset)) {
            return targetAsset;
        }
        return getDefaultMediaToReplace(scene);
    }
    return getDefaultMediaToReplace(scene);
}

export function setSceneDuration(luminary: SafeLuminary, sceneId: string, duration: number): void {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        return;
    }
    scene.duration = duration;
}

export function setMediaTrim(
    luminary: SafeLuminary,
    sceneId: string,
    assetName: string,
    startTime: number,
    stopTime: number,
): void {
    // set the scene duration & media asset trim times
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        return;
    }

    const mediaAsset = scene.getAssetByName(assetName) as TimeAwareMediaAsset;
    if (!mediaAsset) {
        return;
    }

    // When trimming media on a voice driven scene do not change scene duration
    // because its controlled by the transcript
    const sceneGroup = scene.sceneGroup;
    if (!sceneGroup) {
        return;
    }
    const isVoiceDriven = isVoiceDrivenSceneGroup(sceneGroup);

    // TODO: better logic for setting the scene duration if >1 editable video...
    scene.batchRun((batchedScene) => {
        if (!isVoiceDriven) {
            batchedScene.duration = Math.round(stopTime) - Math.round(startTime);
        }
        if (!mediaAsset.linkToSceneGroup) {
            mediaAsset.startTime = Math.round(startTime);
            mediaAsset.endTime = Math.round(stopTime);
        }
    });
}

export function setMediaCrop(
    luminary: SafeLuminary,
    sceneId: string,
    assetId: string,
    offsetX: number,
    offsetY: number,
    scale = 1,
    rotation = 0,
    flipHorizontally = false,
    flipVertically = false,
): void {
    // set the translation property (aka crop)
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        return;
    }

    const asset = scene.getAssetById(assetId);
    if (!asset) {
        return;
    }

    if (VideoData.isMediaAsset(asset) || VideoData.isIconAsset(asset)) {
        asset.xTranslation = offsetX;
        asset.yTranslation = offsetY;
        asset.scaleX = scale;
        asset.scaleY = scale;
        asset.transformation.angle = rotation;
        asset.flipHorizontally = flipHorizontally;
        asset.flipVertically = flipVertically;
    }
}

export function setMediaKenburn(
    luminary: SafeLuminary,
    sceneId: string,
    assetId: string,
    animation: string,
): string | undefined {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        return;
    }

    const mediaAsset = scene.getAssetById(assetId) as TimeAwareMediaAsset | undefined;
    if (!mediaAsset) {
        return;
    }

    const previousBackgroundAnimation = mediaAsset.kenburn;
    mediaAsset.kenburn = animation;
    return previousBackgroundAnimation;
}

/**
 * This function updates the aspect ratio of the luminary to the specified aspect ratio.
 */
export function updateLuminaryAspectRatio(luminary: SafeLuminary, aspectRatio: AspectRatio | null) {
    if (!aspectRatio) {
        return;
    }
    if (aspectRatio !== luminary.aspectRatio) {
        luminary.aspectRatio = aspectRatio;
    }
}
